// src/components/SiteManager.jsx
//
// Admin tool for the site layer:
//   - create yachts / events
//   - assign Peplink + Starlink devices to them
//   - link a site to a client account group
//
// Replaces the "assign router to client" flow. Devices now belong to a
// site, and the client sees a site. Linking one site grants access to
// every device on it, instead of assigning device by device.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import '../styles/fleet-theme.css';
import './SiteManager.css';
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';

const SITE_TYPES = [
  { value: 'yacht', label: 'Yacht' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
];

export default function SiteManager({ user, onLogout }) {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [unassigned, setUnassigned] = useState({ peplink: [], starlink: [] });
  const [selectedSite, setSelectedSite] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteType, setNewSiteType] = useState('yacht');
  const [showCreate, setShowCreate] = useState(false);
  const [unassignedQuery, setUnassignedQuery] = useState('');
  const [siteQuery, setSiteQuery] = useState('');
  const [renamingSiteId, setRenamingSiteId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Billing link: the Supabase yacht UUID whose paid invoices define the
  // client's Starlink plan (e.g. 1 TB) on the client Starlink page.
  const [yachtLink, setYachtLink] = useState(null);       // { yacht_id, yacht_name, quota }
  const [yachts, setYachts] = useState({ status: 'loading', yachts: [] });
  const [yachtFilter, setYachtFilter] = useState('');
  const [yachtChoice, setYachtChoice] = useState('');
  const [yachtPreview, setYachtPreview] = useState(null);  // { yacht_id, quota }
  const [yachtChecking, setYachtChecking] = useState(false);
  const [matches, setMatches] = useState([]);              // name-match suggestions
  const [showMatches, setShowMatches] = useState(true);

  const notify = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadAll = useCallback(async () => {
    try {
      const [siteRes, clientRes, unassignedRes] = await Promise.all([
        apiFetchJSON('/api/v2/admin/sites'),
        apiFetchJSON('/api/v2/admin/sites/clients'),
        apiFetchJSON('/api/v2/admin/sites/unassigned'),
      ]);
      setSites(siteRes.sites || []);
      setClients(clientRes.clients || []);
      setUnassigned(unassignedRes || { peplink: [], starlink: [] });
    } catch (e) {
      notify(e.message || 'Failed to load', true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadSuggestions = useCallback(async (siteId) => {
    try {
      const res = await apiFetchJSON(`/api/v2/admin/sites/${siteId}/suggestions`);
      setSuggestions(res.suggestions || []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const loadYachtLink = useCallback(async (siteId) => {
    setYachtLink(null);
    try {
      setYachtLink(await apiFetchJSON(`/api/v2/admin/sites/${siteId}/yacht-link`));
    } catch {
      setYachtLink({ yacht_id: null, yacht_name: null, quota: null });
    }
  }, []);

  // Supabase yachts + name-match suggestions. Loaded separately from
  // loadAll() so a slow/misconfigured billing lookup never blocks the page.
  const loadBilling = useCallback(async () => {
    try {
      const [y, m] = await Promise.all([
        apiFetchJSON('/api/v2/admin/supabase/yachts'),
        apiFetchJSON('/api/v2/admin/sites/yacht-suggestions'),
      ]);
      setYachts(y || { status: 'error', yachts: [] });
      setMatches(m?.suggestions || []);
    } catch (e) {
      setYachts({ status: 'error', error: e.message, yachts: [] });
      setMatches([]);
    }
  }, []);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const selectSite = (site) => {
    setSelectedSite(site);
    loadSuggestions(site.site_id);
    if (site.site_id !== selectedSite?.site_id) {
      setYachtFilter('');
      setYachtChoice('');
      setYachtPreview(null);
      loadYachtLink(site.site_id);
    }
  };

  async function chooseYacht(id) {
    setYachtChoice(id);
    setYachtPreview(null);
    if (!id) return;
    setYachtChecking(true);
    try {
      setYachtPreview(await apiFetchJSON(`/api/v2/admin/supabase/yacht-quota/${encodeURIComponent(id)}`));
    } catch (e) {
      notify(e.message || 'Could not read billing for that yacht', true);
    } finally {
      setYachtChecking(false);
    }
  }

  async function linkYacht(siteId, yachtId, yachtName, siteName) {
    if (!siteId || !yachtId) return;
    setBusy(true);
    try {
      const res = await apiFetchJSON(`/api/v2/admin/sites/${siteId}/yacht-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ yacht_id: yachtId }),
      });
      if (selectedSite?.site_id === siteId) {
        setYachtLink({ yacht_id: res.yacht_id, yacht_name: res.yacht_name, quota: res.quota });
        setYachtChoice('');
        setYachtPreview(null);
      }
      notify(`${yachtName || 'Yacht'} linked to ${siteName || 'site'}`);
      await Promise.all([loadAll(), loadBilling()]);
    } catch (e) {
      notify(e.message || 'Could not link yacht', true);
    } finally {
      setBusy(false);
    }
  }

  async function unlinkYacht() {
    if (!selectedSite) return;
    if (!window.confirm('Unlink the billing yacht? The client will see Starlink usage without a plan limit until a yacht is linked again.')) return;
    setBusy(true);
    try {
      await apiFetchJSON(`/api/v2/admin/sites/${selectedSite.site_id}/yacht-link`, { method: 'DELETE' });
      setYachtLink({ yacht_id: null, yacht_name: null, quota: null });
      notify('Billing yacht unlinked');
      await Promise.all([loadAll(), loadBilling()]);
    } catch (e) {
      notify(e.message || 'Could not unlink yacht', true);
    } finally {
      setBusy(false);
    }
  }

  async function dismissMatch(m) {
    setMatches((prev) => prev.filter((x) => !(x.site_id === m.site_id && x.yacht_id === m.yacht_id)));
    try {
      await apiFetchJSON('/api/v2/admin/sites/yacht-suggestions/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: m.site_id, yacht_id: m.yacht_id }),
      });
      loadBilling(); // the site may now get its next-best match
    } catch (e) {
      notify(e.message || 'Could not dismiss', true);
    }
  }

  async function createSite(e) {
    e.preventDefault();
    if (!newSiteName.trim()) return;

    setBusy(true);
    try {
      await apiFetchJSON('/api/v2/admin/sites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_name: newSiteName, site_type: newSiteType }),
      });
      setNewSiteName('');
      setShowCreate(false);
      notify(`Site "${newSiteName}" created`);
      await loadAll();
    } catch (e) {
      notify(e.message || 'Could not create site', true);
    } finally {
      setBusy(false);
    }
  }

  function startRename(site) {
    setRenamingSiteId(site.site_id);
    setRenameValue(site.site_name);
  }

  async function confirmRename(siteId) {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      notify('Site name cannot be empty', true);
      return;
    }

    setBusy(true);
    try {
      await apiFetchJSON(`/api/v2/admin/sites/${siteId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_name: trimmed }),
      });
      setRenamingSiteId(null);
      notify('Site renamed');
      await loadAll();
      if (selectedSite?.site_id === siteId) {
        const refreshed = (await apiFetchJSON('/api/v2/admin/sites')).sites
          .find((s) => s.site_id === siteId);
        setSelectedSite(refreshed || null);
      }
    } catch (e) {
      notify(e.message || 'Could not rename site', true);
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteSite(site) {
    if (!window.confirm(
      `Delete "${site.site_name}"? Its ${site.devices?.length || 0} device(s) will become unassigned, not deleted - you can re-assign them elsewhere afterward.`
    )) {
      return;
    }

    setBusy(true);
    try {
      await apiFetchJSON(`/api/v2/admin/sites/${site.site_id}`, { method: 'DELETE' });
      notify(`"${site.site_name}" deleted`);
      if (selectedSite?.site_id === site.site_id) {
        setSelectedSite(null);
      }
      await loadAll();
    } catch (e) {
      notify(e.message || 'Could not delete site', true);
    } finally {
      setBusy(false);
    }
  }

  async function assignDevice(device) {
    if (!selectedSite) {
      notify('Pick a site on the left first', true);
      return;
    }

    setBusy(true);
    try {
      await apiFetchJSON(`/api/v2/admin/sites/${selectedSite.site_id}/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_type: device.device_type,
          device_key: device.device_key,
          label: device.name,
          source: device.service,
        }),
      });
      notify(`${device.name} → ${selectedSite.site_name}`);
      await loadAll();
      const refreshed = (await apiFetchJSON('/api/v2/admin/sites')).sites
        .find((s) => s.site_id === selectedSite.site_id);
      setSelectedSite(refreshed || null);
      if (refreshed) loadSuggestions(refreshed.site_id);
    } catch (e) {
      notify(e.message || 'Assign failed', true);
    } finally {
      setBusy(false);
    }
  }

  async function unassignDevice(device) {
    setBusy(true);
    try {
      await apiFetchJSON('/api/v2/admin/sites/devices', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_type: device.device_type,
          device_key: device.device_key,
        }),
      });
      notify('Device unassigned');
      await loadAll();
      const refreshed = (await apiFetchJSON('/api/v2/admin/sites')).sites
        .find((s) => s.site_id === selectedSite.site_id);
      setSelectedSite(refreshed || null);
    } catch (e) {
      notify(e.message || 'Unassign failed', true);
    } finally {
      setBusy(false);
    }
  }

  async function linkClient(clientId) {
    if (!selectedSite) return;

    setBusy(true);
    try {
      if (clientId) {
        await apiFetchJSON(`/api/v2/admin/sites/${selectedSite.site_id}/link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: Number(clientId) }),
        });
        notify('Client linked - they can now see every device on this site');
      } else {
        await apiFetchJSON(`/api/v2/admin/sites/${selectedSite.site_id}/link`, {
          method: 'DELETE',
        });
        notify('Client unlinked');
      }
      await loadAll();
      const refreshed = (await apiFetchJSON('/api/v2/admin/sites')).sites
        .find((s) => s.site_id === selectedSite.site_id);
      setSelectedSite(refreshed || null);
    } catch (e) {
      notify(e.message || 'Link failed', true);
    } finally {
      setBusy(false);
    }
  }

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  // Example site: prefer one already linked to a client and with devices,
  // so every part of the detail panel has real content to point at.
  const tourSite =
    sites.find((s) => s.client_id && (s.devices || []).length > 0) ||
    sites.find((s) => (s.devices || []).length > 0) ||
    sites[0] || null;
  const tourSelectedBeforeRef = useRef(null);
  usePageTour('site-manager', {
    sites,
    tourSite,
    tourClientName: tourSite?.client_id
      ? (clients.find((c) => c.client_id === tourSite.client_id)?.client_name || null)
      : null,
    unassignedCount: unassigned.peplink.length + unassigned.starlink.length,
    matchCount: matches.length,
    selectTourSite: () => {
      if (tourSite && selectedSite?.site_id !== tourSite.site_id) selectSite(tourSite);
    },
    onTourStart: () => {
      tourSelectedBeforeRef.current = selectedSite;
      setShowCreate(false);
      setRenamingSiteId(null);
    },
    onTourEnd: () => {
      const before = tourSelectedBeforeRef.current;
      if (before) { if (before.site_id !== selectedSite?.site_id) selectSite(before); }
      else setSelectedSite(null);
    },
  }, { ready: !loading });

  if (loading) return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading site manager…</p>
          </div>
        </div>
  );

  return (
    <div className="sm-page">
      <FleetHeader title="Site Manager" user={user} onLogout={onLogout} />
      <div className="sm-wrap">
      {message && (
        <div className={`ft-toolbar-${message.isError ? 'error' : 'success'}`} role="alert">
            <i className={`fas fa-${message.isError ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {matches.length > 0 && (
        <div className="sm-panel" style={{ marginBottom: 16 }} data-tour="sm-matches">
          <div className="sm-panel-head">
            <h5>BILLING MATCHES</h5>
            <div className="sm-panel-head-right">
              <span className="sm-count">{matches.length}</span>
              <button className="sm-btn-pill" onClick={() => setShowMatches((v) => !v)}>
                {showMatches ? 'HIDE' : 'REVIEW'}
              </button>
            </div>
          </div>
          {showMatches && (
            <>
              <p className="sm-help" style={{ margin: '0 0 8px' }}>
                Sites without a billing yacht, paired with a Nautilus CRM yacht of a similar name.
                Approving links them, so the client's Starlink page shows the plan from that yacht's invoices.
              </p>
              <div className="sm-list">
                {matches.map((m) => (
                  <div key={m.site_id + m.yacht_id} className="sm-device-row suggestion">
                    <span className={'sm-kind ' + (m.has_starlink ? 'starlink' : 'peplink')}>
                      {m.has_starlink ? 'STARLINK' : 'SITE'}
                    </span>
                    <span className="sm-device-name">
                      <span className="sm-name-row">{m.site_name} ↔ {m.yacht_name}</span>
                      <span className="sm-reason">{m.reason} · {Math.round(m.score * 100)}% match</span>
                    </span>
                    <button
                      className="sm-btn-add"
                      onClick={() => linkYacht(m.site_id, m.yacht_id, m.yacht_name, m.site_name)}
                      disabled={busy}
                    >
                      APPROVE
                    </button>
                    <button className="sm-btn-remove" onClick={() => dismissMatch(m)} disabled={busy}>
                      DISMISS
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="sm-grid">
        {/* Sites */}
        <div className="sm-panel" data-tour="sm-sites">
          <div className="sm-panel-head">
            <h5>SITES</h5>
            <div className="sm-panel-head-right">
              <span className="sm-count-plain">{sites.length}</span>
              <button className="sm-btn-pill" onClick={() => setShowCreate((s) => !s)}>
                {showCreate ? 'CANCEL' : '+ NEW'}
              </button>
            </div>
          </div>

          {showCreate && (
            <form className="sm-create" onSubmit={createSite}>
              <input
                placeholder="Site name (e.g. Yacht Aurora)"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                autoFocus
              />
              <select value={newSiteType} onChange={(e) => setNewSiteType(e.target.value)}>
                {SITE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <button type="submit" disabled={busy}>Create</button>
            </form>
          )}

          <div className="sm-search-wrap">
            <input
              className="sm-search-input"
              placeholder="filter sites"
              value={siteQuery}
              onChange={(e) => setSiteQuery(e.target.value)}
            />
            {siteQuery && (
              <button className="sm-search-clear" onClick={() => setSiteQuery('')} aria-label="Clear search">
                ×
              </button>
            )}
          </div>

          <div className="sm-list">
            {sites
              .filter((s) => {
                const q = siteQuery.trim().toLowerCase();
                return !q || s.site_name.toLowerCase().includes(q);
              })
              .map((s) => (
              <div
                key={s.site_id}
                className={
                  'sm-site-row' +
                  (selectedSite?.site_id === s.site_id ? ' selected' : '')
                }
                data-tour={tourSite && s.site_id === tourSite.site_id ? 'sm-site' : undefined}
                onClick={() => selectSite(s)}
              >
                <div className="sm-site-main">
                  {s.has_suggestions && (
                    <span
                      className="sm-suggestion-dot"
                      title="This site has unassigned devices that might belong to it"
                    />
                  )}
                  <div className="sm-site-info">
                    <div className="sm-site-title-row">
                      <span className="sm-site-name">{s.site_name}</span>
                      <span className={'sm-type ' + s.site_type}>{s.site_type}</span>
                    </div>
                    <div className={'sm-site-sub' + (s.client_id ? ' linked' : '')}>
                      {s.devices?.length || 0} device{(s.devices?.length || 0) !== 1 ? 's' : ''}
                      {s.client_id ? ' · linked' : ' · no client'}
                      {s.supabase_yacht_id ? ' · billing' : ''}
                    </div>
                  </div>
                  <span
                    className="sm-edit-link"
                    onClick={(e) => { e.stopPropagation(); selectSite(s); startRename(s); }}
                  >
                    EDIT
                  </span>
                </div>
              </div>
            ))}
            {sites.length === 0 && (
              <div className="sm-empty">No sites yet — create your first one.</div>
            )}
            {sites.length > 0 && siteQuery.trim() && !sites.some((s) => s.site_name.toLowerCase().includes(siteQuery.trim().toLowerCase())) && (
              <div className="sm-search-empty">No matches</div>
            )}
          </div>
        </div>

        {/* Selected site detail */}
        <div className="sm-panel" data-tour="sm-detail">
          <div className="sm-panel-head">
            <div>
              <div className="sm-eyebrow">SELECTED SITE</div>
              {selectedSite && renamingSiteId === selectedSite.site_id ? (
                <div className="sm-rename-row">
                  <input
                    className="sm-rename-input"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmRename(selectedSite.site_id);
                      if (e.key === 'Escape') setRenamingSiteId(null);
                    }}
                    autoFocus
                  />
                  <button className="sm-btn-small" onClick={() => confirmRename(selectedSite.site_id)}>Save</button>
                  <button className="sm-btn-small" onClick={() => setRenamingSiteId(null)}>Cancel</button>
                </div>
              ) : (
                <h5 className="sm-selected-site-name">{selectedSite ? selectedSite.site_name : 'Select a site'}</h5>
              )}
            </div>
            {selectedSite && renamingSiteId !== selectedSite.site_id && (
              <div className="sm-detail-actions">
                <button className="sm-btn-outline" onClick={() => startRename(selectedSite)}>RENAME</button>
                <button className="sm-btn-danger" onClick={() => handleDeleteSite(selectedSite)}>DELETE SITE</button>
              </div>
            )}
          </div>

          {!selectedSite && (
            <div className="sm-empty">
              Pick a site to manage its devices and client access.
            </div>
          )}

          {selectedSite && (
            <div className="sm-detail">
              <div className="sm-field" data-tour="sm-client">
                <label>CLIENT ACCESS</label>
                <select
                  value={selectedSite.client_id || ''}
                  onChange={(e) => linkClient(e.target.value)}
                  disabled={busy}
                >
                  <option value="">— NO CLIENT LINKED —</option>
                  {clients.map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.client_name}
                    </option>
                  ))}
                </select>
                <p className="sm-help">
                  Linking grants that client every device on this site, now and in future.
                </p>
              </div>

              <div className="sm-field" data-tour="sm-billing">
                <label>BILLING YACHT (Nautilus CRM)</label>
                {yachtLink === null ? (
                  <div className="sm-empty small">Loading…</div>
                ) : yachtLink.yacht_id ? (
                  <>
                    <div className="sm-device-row">
                      <span className="sm-device-name">
                        <span className="sm-name-row">{yachtLink.yacht_name || 'Linked yacht'}</span>
                        <span className="sm-reason" style={{ fontFamily: 'monospace' }}>{yachtLink.yacht_id}</span>
                      </span>
                      <button className="sm-btn-remove" onClick={unlinkYacht} disabled={busy}>UNLINK</button>
                    </div>
                    <QuotaSummary quota={yachtLink.quota} />
                  </>
                ) : yachts.status === 'ok' ? (
                  (() => {
                    const match = matches.find((m) => m.site_id === selectedSite.site_id);
                    const q = yachtFilter.trim().toLowerCase();
                    const options = yachts.yachts.filter((y) =>
                      !y.linked_site_id && (!q || y.name.toLowerCase().includes(q))
                    );
                    const chosen = yachts.yachts.find((y) => y.id === yachtChoice);
                    return (
                      <>
                        {match && !yachtChoice && (
                          <p className="sm-help">
                            Suggested: <strong>{match.yacht_name}</strong> ({Math.round(match.score * 100)}% name match).{' '}
                            <span className="sm-add-all" onClick={() => chooseYacht(match.yacht_id)}>USE THIS</span>
                          </p>
                        )}
                        <input
                          className="sm-search-input"
                          placeholder="filter yachts by name"
                          value={yachtFilter}
                          onChange={(e) => setYachtFilter(e.target.value)}
                          aria-label="Filter Nautilus CRM yachts"
                        />
                        <select
                          value={yachtChoice}
                          onChange={(e) => chooseYacht(e.target.value)}
                          disabled={busy}
                          aria-label="Nautilus CRM yacht"
                          style={{ marginTop: 6 }}
                        >
                          <option value="">
                            {options.length ? `— choose a yacht (${options.length}) —` : '— no unlinked yachts match —'}
                          </option>
                          {chosen && !options.includes(chosen) && <option value={chosen.id}>{chosen.name}</option>}
                          {options.map((y) => (
                            <option key={y.id} value={y.id}>{y.name}</option>
                          ))}
                        </select>
                        {yachtChecking && <p className="sm-help">Reading this yacht's invoices…</p>}
                        {yachtPreview && <QuotaSummary quota={yachtPreview.quota} preview />}
                        {yachtChoice && (
                          <button
                            className="sm-btn-small"
                            style={{ marginTop: 6 }}
                            onClick={() => linkYacht(selectedSite.site_id, yachtChoice, chosen?.name, selectedSite.site_name)}
                            disabled={busy}
                          >
                            Link {chosen?.name || 'yacht'}
                          </button>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <p className="sm-help">
                    {yachts.status === 'loading'
                      ? 'Loading yachts from billing…'
                      : yachts.status === 'not_configured'
                        ? "Billing isn't connected: set SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY on the server."
                        : `Couldn't load yachts from billing. ${yachts.error || ''}`}
                  </p>
                )}
                <p className="sm-help">
                  The client's Starlink plan and boosters are read from this yacht's paid invoices.
                  Without a link, the client sees usage with no plan limit.
                </p>
              </div>

              <div className="sm-field" data-tour="sm-devices">
                <div className="sm-field-head">
                  <label>DEVICES ON THIS SITE</label>
                  <span className="sm-field-count">
                    {(selectedSite.devices || []).filter((d) => d.device_type === 'peplink').length} PEPLINK ·{' '}
                    {(selectedSite.devices || []).filter((d) => d.device_type === 'starlink').length} STARLINK
                  </span>
                </div>
                {(selectedSite.devices || []).length === 0 && (
                  <div className="sm-empty small">Nothing assigned yet</div>
                )}
                {(selectedSite.devices || []).map((d) => (
                  <div key={d.device_type + d.device_key} className="sm-device-row">
                    <span className={'sm-kind ' + d.device_type}>
                      {d.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}
                    </span>
                    <span className="sm-device-name">
                      <span className="sm-name-row">
                        {d.label || d.device_key}
                        {/* d.service comes from describe() (ELCOME cache presence).
                            The old d.source check never fired: /admin/sites
                            doesn't return the source column. */}
                        {d.device_type === 'starlink' && d.service === 'roam' && <span className="sm-roam">ROAM</span>}
                        {d.device_type === 'starlink' && d.service === 'missing' && (
                          <span className="sm-roam" title={`No longer returned by ELCOME${d.missing_since ? ` since ${d.missing_since}` : ''}`}>MISSING</span>
                        )}
                      </span>
                      {d.connected_via_peplink && (
                        <span className="sm-reason">
                          via {d.connected_via_peplink}
                          {/* backend (_describe_device) sends wan_interface_name, not connected_via_wan */}
                          {(d.wan_interface_name || d.connected_via_wan) && ` (${d.wan_interface_name || d.connected_via_wan})`}
                        </span>
                      )}
                    </span>
                    <button
                      className="sm-btn-remove"
                      onClick={() => unassignDevice(d)}
                      disabled={busy}
                    >
                      REMOVE
                    </button>
                  </div>
                ))}
              </div>

              <div className="sm-assign-hint">
                <div className="sm-eyebrow accent">ADD FROM UNASSIGNED</div>
                <p>Use Add on any device in the Unassigned panel to assign it here. Starlink kits uplinked through a Peplink already on this site are matched automatically under Suggested below.</p>
              </div>

              {suggestions.length > 0 && (
                <div className="sm-field">
                  <label>Suggested</label>
                  <p className="sm-help">
                    Found via a Peplink WAN link, or a device name matching this site.
                  </p>
                  {suggestions.map((s) => (
                    <div key={s.device_key} className="sm-device-row suggestion">
                      <span className={'sm-kind ' + s.device_type}>
                        {s.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}
                      </span>
                      <span className="sm-device-name">
                        {s.name}
                        <span className="sm-reason">{s.reason}</span>
                      </span>
                      <button
                        className="sm-btn-add"
                        onClick={() => assignDevice(s)}
                        disabled={busy}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Unassigned pool */}
        <div className="sm-panel" data-tour="sm-unassigned">
          <div className="sm-panel-head">
            <h5>UNASSIGNED</h5>
            <span className="sm-count">
              {unassigned.peplink.length + unassigned.starlink.length}
            </span>
          </div>

          <div className="sm-search-wrap">
            <input
              className="sm-search-input"
              placeholder="name or serial"
              value={unassignedQuery}
              onChange={(e) => setUnassignedQuery(e.target.value)}
            />
            {unassignedQuery && (
              <button className="sm-search-clear" onClick={() => setUnassignedQuery('')} aria-label="Clear search">
                ×
              </button>
            )}
          </div>

          <div className="sm-list">
            {(() => {
              const q = unassignedQuery.trim().toLowerCase();
              const matches = (d) =>
                !q ||
                (d.name || '').toLowerCase().includes(q) ||
                (d.device_key || '').toLowerCase().includes(q);

              const filteredPeplink = unassigned.peplink.filter(matches);
              const filteredStarlink = unassigned.starlink.filter(matches);

              const addAll = async (list) => {
                if (!selectedSite || busy) return;
                setBusy(true);
                try {
                  for (const d of list) {
                    await apiFetchJSON(`/api/v2/admin/sites/${selectedSite.site_id}/devices`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        device_type: d.device_type,
                        device_key: d.device_key,
                        label: d.name,
                        source: d.service,
                      }),
                    });
                  }
                  notify(`${list.length} device${list.length !== 1 ? 's' : ''} → ${selectedSite.site_name}`);
                  await loadAll();
                  const refreshed = (await apiFetchJSON('/api/v2/admin/sites')).sites
                    .find((s) => s.site_id === selectedSite.site_id);
                  setSelectedSite(refreshed || null);
                  if (refreshed) loadSuggestions(refreshed.site_id);
                } catch (e) {
                  notify(e.message || 'Bulk add failed', true);
                } finally {
                  setBusy(false);
                }
              };

              return (
                <>
                  <div className="sm-group-header">
                    <span className="sm-group-title">PEPLINK</span>
                    <span className="sm-group-count">{filteredPeplink.length}</span>
                    {filteredPeplink.length > 0 && selectedSite && (
                      <span className="sm-add-all" onClick={() => addAll(filteredPeplink)}>ADD ALL</span>
                    )}
                  </div>
                  {filteredPeplink.length === 0 && (
                    <div className="sm-search-empty">{q ? 'No matches' : 'None unassigned'}</div>
                  )}
                  {filteredPeplink.map((d) => (
                    <div key={d.device_key} className="sm-device-row">
                      <span className="sm-unassigned-dot" />
                      <span className="sm-device-name">{d.name}</span>
                      <button
                        className="sm-btn-add"
                        onClick={() => assignDevice(d)}
                        disabled={busy || !selectedSite}
                      >
                        ADD
                      </button>
                    </div>
                  ))}

                  <div className="sm-group-header">
                    <span className="sm-group-title">STARLINK</span>
                    <span className="sm-group-count">{filteredStarlink.length}</span>
                    {filteredStarlink.length > 0 && selectedSite && (
                      <span className="sm-add-all" onClick={() => addAll(filteredStarlink)}>ADD ALL</span>
                    )}
                  </div>
                  {filteredStarlink.length === 0 && (
                    <div className="sm-search-empty">{q ? 'No matches' : 'None unassigned'}</div>
                  )}
                  {filteredStarlink.map((d) => (
                    <div key={d.device_key} className="sm-device-row">
                      <span className="sm-unassigned-dot" />
                      <span className="sm-device-name">
                        <span className="sm-name-row">
                          {d.name}
                          {d.service === 'roam' && <span className="sm-roam">ROAM</span>}
                          {d.service === 'missing' && (
                            <span className="sm-roam" title={`No longer returned by ELCOME${d.missing_since ? ` since ${d.missing_since}` : ''}`}>MISSING</span>
                          )}
                        </span>
                        {/* ROAM entries only exist because a Peplink reported this
                            dish on a WAN in the last 24h - show which one, so the
                            dish can be traced (and assigned to that Peplink's site). */}
                        {d.behind_peplink && (
                          <span className="sm-reason" title="Peplink that reported this dish on a WAN in the last 24 hours">
                            via {d.behind_peplink}
                            {d.wan_interface_name && ` (${d.wan_interface_name})`}
                          </span>
                        )}
                      </span>
                      <button
                        className="sm-btn-add"
                        onClick={() => assignDevice(d)}
                        disabled={busy || !selectedSite}
                      >
                        ADD
                      </button>
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// One-line readout of what a Supabase yacht resolves to this month.
function QuotaSummary({ quota, preview = false }) {
  if (!quota) return null;
  const gb = (n) => (n >= 1000 ? `${+(n / 1000).toFixed(2)} TB` : `${+Number(n).toFixed(2)} GB`);

  if (quota.status === 'not_configured') {
    return <p className="sm-help">Billing lookup is off: SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY aren't set on the server.</p>;
  }
  if (quota.status === 'error') {
    return <p className="sm-help">Billing lookup failed: {quota.error || 'unknown error'}.</p>;
  }
  if (quota.status === 'no_plan') {
    return (
      <p className="sm-help">
        {preview ? 'This yacht ID has no' : 'No'} paid Starlink Maritime plan found this month
        {quota.booster_gb ? ` (boosters: ${gb(quota.booster_gb)})` : ''}.
        {preview ? ' Check the ID, or link it anyway if the invoice is still being paid.' : ''}
      </p>
    );
  }
  return (
    <p className="sm-help">
      This month: <strong>{quota.plan_name || `Starlink Maritime ${gb(quota.plan_gb)}`}</strong>
      {quota.booster_gb ? ` + ${gb(quota.booster_gb)} in boosters` : ''}
      {' '}= {gb(quota.total_gb)} for the client (invoice {quota.plan_invoice}).
      {quota.stale ? ' Last good read shown; the latest lookup failed.' : ''}
    </p>
  );
}
